import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
} from "docx";



interface ExportData {
  candidateData: any;
  aiAnalysis: any;
  currentStatus: string;
  statusHistory: any[];
}


/*
|--------------------------------------------------------------------------
| Export PDF
|--------------------------------------------------------------------------
*/

export const exportToPDF = (
  data: ExportData
) => {

  const {
    candidateData,
    aiAnalysis,
    currentStatus,
    statusHistory,
  } = data;


  const doc =
    new jsPDF();


  doc.setFontSize(18);

  doc.text(
    "Candidate Evaluation Report",
    14,
    20
  );


  doc.setFontSize(12);


  doc.text(
    `Candidate Name: ${candidateData?.name || "-"}`,
    14,
    35
  );


  doc.text(
    `Position: ${candidateData?.position || "-"}`,
    14,
    45
  );


  doc.text(
    `Current Status: ${currentStatus}`,
    14,
    55
  );



  autoTable(doc, {

    startY: 70,

    head: [
      [
        "AI Parameter",
        "Value",
      ],
    ],


    body: [

      [
        "ATS Score",
        `${aiAnalysis?.atsScore || 0}%`,
      ],

      [
        "Resume Score",
        `${aiAnalysis?.resumeScore || 0}%`,
      ],

      [
        "Video Score",
        `${aiAnalysis?.videoScore || 0}%`,
      ],

      [
        "Job Match Score",
        `${aiAnalysis?.jobMatchScore || 0}%`,
      ],

      [
        "Recommendation",
        aiAnalysis?.recommendation || "-",
      ],

      [
        "Confidence",
        `${aiAnalysis?.confidence || 0}%`,
      ],

    ],

  });



  autoTable(doc, {

    startY:
      (doc as any).lastAutoTable.finalY + 10,


    head: [
      [
        "Interview",
        "Details",
      ],
    ],


    body: [

      [
        "Proceed",
        aiAnalysis
          ?.interviewRecommendation
          ?.proceed
          ? "Yes"
          : "No",
      ],


      [
        "Next Round",
        aiAnalysis
          ?.interviewRecommendation
          ?.nextRound ||
        aiAnalysis
          ?.interviewRecommendation
          ?.round ||
        "-",
      ],


      [
        "Reason",
        aiAnalysis
          ?.interviewRecommendation
          ?.reason ||
        "-",
      ],

    ],

  });



  autoTable(doc, {

    startY:
      (doc as any).lastAutoTable.finalY + 10,


    head: [
      [
        "Status",
        "Date",
      ],
    ],


    body:

      statusHistory?.map(
        (item:any)=>[
          item.status || "-",
          item.date || "-",
        ]
      ) || [],

  });



  doc.save(
    `${candidateData?.name || "candidate"}-report.pdf`
  );

};




/*
|--------------------------------------------------------------------------
| Export Excel
|--------------------------------------------------------------------------
*/

export const exportToExcel = (
  data: ExportData
) => {


  const {
    candidateData,
    aiAnalysis,
    currentStatus,
    statusHistory,
  } = data;



  const workbook =
    XLSX.utils.book_new();



  const candidateSheet =
    XLSX.utils.json_to_sheet([

      {
        Name:
          candidateData?.name || "-",

        Email:
          candidateData?.email || "-",

        Phone:
          candidateData?.phone || "-",

        Position:
          candidateData?.position || "-",

        Status:
          currentStatus,

      },

    ]);



  XLSX.utils.book_append_sheet(
    workbook,
    candidateSheet,
    "Candidate Details"
  );



  const aiSheet =
    XLSX.utils.json_to_sheet([

      {
        ATS:
          aiAnalysis?.atsScore || 0,

        Resume:
          aiAnalysis?.resumeScore || 0,

        Video:
          aiAnalysis?.videoScore || 0,

        JobMatch:
          aiAnalysis?.jobMatchScore || 0,

        Recommendation:
          aiAnalysis?.recommendation || "-",

        Confidence:
          aiAnalysis?.confidence || 0,

      },

    ]);



  XLSX.utils.book_append_sheet(
    workbook,
    aiSheet,
    "AI Analysis"
  );



  const skillsSheet =
    XLSX.utils.json_to_sheet(

      aiAnalysis?.matchedSkills?.map(
        (skill:string)=>({
          MatchedSkill:
            skill,
        })
      ) || []

    );



  XLSX.utils.book_append_sheet(
    workbook,
    skillsSheet,
    "Skills"
  );



  const interviewSheet =
    XLSX.utils.json_to_sheet([

      {

        NextRound:
          aiAnalysis
            ?.interviewRecommendation
            ?.nextRound || "-",


        Reason:
          aiAnalysis
            ?.interviewRecommendation
            ?.reason || "-",


      },

    ]);



  XLSX.utils.book_append_sheet(
    workbook,
    interviewSheet,
    "Interview"
  );



  const statusSheet =
    XLSX.utils.json_to_sheet(
      statusHistory || []
    );



  XLSX.utils.book_append_sheet(
    workbook,
    statusSheet,
    "Status History"
  );



  const excelBuffer =
    XLSX.write(
      workbook,
      {
        bookType:"xlsx",
        type:"array",
      }
    );


  const blob =
    new Blob(
      [
        excelBuffer,
      ],
      {
        type:
        "application/octet-stream",
      }
    );


  saveAs(
    blob,
    `${candidateData?.name || "candidate"}-report.xlsx`
  );

};




/*
|--------------------------------------------------------------------------
| Export Word
|--------------------------------------------------------------------------
*/

export const exportToDoc = async (
  data: ExportData
) => {


  const {
    candidateData,
    aiAnalysis,
    currentStatus,
  } = data;



  const document =
    new Document({

      sections:[
        {

          children:[


            new Paragraph({

              children:[
                new TextRun({

                  text:
                  "Candidate Evaluation Report",

                  bold:true,

                  size:32,

                }),
              ],

            }),



            new Paragraph(
              `Candidate Name: ${
                candidateData?.name || "-"
              }`
            ),


            new Paragraph(
              `Position: ${
                candidateData?.position || "-"
              }`
            ),


            new Paragraph(
              `Status: ${
                currentStatus
              }`
            ),



            new Paragraph(
              `ATS Score: ${
                aiAnalysis?.atsScore || 0
              }%`
            ),



            new Paragraph(
              `Recommendation: ${
                aiAnalysis?.recommendation || "-"
              }`
            ),



            new Paragraph(
              `Interview Round: ${
                aiAnalysis
                ?.interviewRecommendation
                ?.nextRound || "-"
              }`
            ),

          ],

        },

      ],

    });



  const buffer =
    await Packer.toBlob(
      document
    );


  saveAs(
    buffer,
    `${candidateData?.name || "candidate"}-report.docx`
  );

};